import {i18n} from '@online-offer/i18n';
import {DataColumn, SectionPrint} from '@online-offer/shared';
import {Heading, HStack, Show, Text, VStack} from 'platform/foundation';
import {formatNumber, useDateTimeFormatter, useFormatCurrency, useLocale} from 'platform/locale';
import {match} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';

import {FinancialProductOffer} from '../../api/types/onlineOfferTypes';
import {useFinancialProducts} from '../../hooks/useFinancialProducts';
import {formatFinancialProductMoney} from './formatFinancialProductMoney';
import {formatFinancialProductPercentage} from './formatFinancialProductPercentage';
import {getFinancialProductTypeLabelKey} from './getFinancialProductTypeLabelKey';
import {getFinancialProductUnavailableReasonLabelKey} from './getFinancialProductUnavailableReasonLabelKey';
import {getFinancialProductValidUntilLabel} from './getFinancialProductValidUntilLabel';
import {getLeasingTargetGroupLabelKey} from './getLeasingTargetGroupLabelKey';

const offerTerms = (offer: FinancialProductOffer, locale: ReturnType<typeof useLocale>) => {
  const selection = offer.selection;

  if (isNil(selection)) {
    return null;
  }

  return match(offer.productType)
    .with('LEASING', () =>
      [
        i18n.t('financialProducts.monthsValue', {value: selection.termMonths}),
        isNil(selection.annualMileage)
          ? null
          : i18n.t('financialProducts.kilometresPerYearValue', {
              value: formatNumber(locale.localeConfig, selection.annualMileage, 0),
            }),
        isNil(selection.leasingTargetGroup)
          ? null
          : i18n.t(getLeasingTargetGroupLabelKey(selection.leasingTargetGroup)),
      ]
        .filter(isNotNil)
        .join(' · ')
    )
    .otherwise(() =>
      [
        i18n.t('financialProducts.monthsValue', {value: selection.termMonths}),
        isNil(selection.downPaymentPercentage)
          ? null
          : `${i18n.t('financialProducts.downPayment')}: ${formatFinancialProductPercentage(
              selection.downPaymentPercentage
            )}`,
        isNil(selection.balloonPaymentPercentage)
          ? null
          : `${i18n.t('financialProducts.balloonPayment')}: ${formatFinancialProductPercentage(
              selection.balloonPaymentPercentage
            )}`,
      ]
        .filter(isNotNil)
        .join(' · ')
    );
};

export function FinancialProductsPrint() {
  const {financialProducts} = useFinancialProducts();
  const formatCurrency = useFormatCurrency();
  const formatDateTime = useDateTimeFormatter();
  const locale = useLocale();

  const offers = [
    financialProducts?.standard,
    financialProducts?.balloon,
    financialProducts?.leasing,
  ].filter(isNotNil);
  const offer = isNil(financialProducts?.selectedProduct)
    ? offers.at(0)
    : offers.find((candidate) => candidate.productType === financialProducts.selectedProduct);

  if (isNil(offer)) {
    return null;
  }

  const calculation = offer.calculation;
  const terms = offerTerms(offer, locale);
  const validUntil = getFinancialProductValidUntilLabel(offer.validTo, formatDateTime);

  return (
    <SectionPrint heading={i18n.t('financialProducts.heading')}>
      <VStack spacing={2}>
        <Show when={isNotNil(calculation)}>
          <HStack spacing={2} align="baseline">
            <Heading size={4} data-testid="financialProductsPrint-monthlyPayment">
              {formatFinancialProductMoney(calculation?.monthlyPayment, formatCurrency)}
            </Heading>
            <Text size="small" color="secondary">
              {i18n.t('financialProducts.perMonth')}
            </Text>
          </HStack>
        </Show>
        <Text size="small" color="secondary">
          {[i18n.t(getFinancialProductTypeLabelKey(offer.productType)), terms]
            .filter(isNotNil)
            .join(' · ')}
        </Text>
        <Show when={offer.availability === 'UNAVAILABLE'}>
          <Text size="small" color="danger" data-testid="financialProductsPrint-unavailable">
            {i18n.t(getFinancialProductUnavailableReasonLabelKey(offer.unavailableReason))}
          </Text>
        </Show>
        <DataColumn
          data={[{label: i18n.t('financialProducts.providedBy'), value: offer.providerName}]}
        />
        <Show when={isNotNil(validUntil)}>
          <Text size="small" color="tertiary">
            {validUntil}
          </Text>
        </Show>
        <Show when={financialProducts?.mode === 'CONTRACT_SNAPSHOT'}>
          <Text size="small" color="tertiary">
            {i18n.t('financialProducts.signedSnapshot')}
          </Text>
        </Show>
      </VStack>
    </SectionPrint>
  );
}
